import { FC, useState } from "react";

import MapView from "src/dynamic-view-renderer/Widgets/GoogleMapsWidget/MapView.tsx";
import StreetView from "src/dynamic-view-renderer/Widgets/GoogleMapsWidget/StreetView.tsx";
import WidgetContainer, {
  WidgetContainerProps,
} from "src/dynamic-view-renderer/Widgets/WidgetContainer.tsx";
import { useT } from "src/internationalization";
import { Case } from "src/types/case.ts";

import MarkerDetails from "./MarkerDetails.tsx";
import { useCompanyInfo } from "./useCompanyInfo.ts";
import { useLocation } from "./useLocation.ts";
import ViewModeSwitchButton from "./ViewModeSwitchButton.tsx";

export type GoogleStreetViewWidgetConfiguration = {
  companyElementPath?: string;
  companyAddressPropertyName?: string;
  companyNamePropertyName?: string;
  companySirenPropertyName?: string;
};

export type GoogleStreetViewWidgetProps = Omit<WidgetContainerProps, "children"> & {
  data?: {
    case: Case;
  };
  configuration?: GoogleStreetViewWidgetConfiguration;
};

const GoogleStreetViewWidget: FC<GoogleStreetViewWidgetProps> = (props) => {
  const { data, configuration, widgetDefinition } = props;

  const caseElements = data ? data.case.elements : [];

  const company = useCompanyInfo(caseElements, configuration);

  const { center, placeLocation, geocodedLocation, placeDetails, geoCoderDetails, error, warning } =
    useLocation({ company, fallbackCenter: { lat: 48.8566, lng: 2.3522 } });

  const [viewMode, setViewMode] = useState<"street" | "map">("street");
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.PlaceResult | null>(null);
  const [selectedGeoCoder, setSelectedGeoCoder] = useState<google.maps.GeocoderResult | null>(null);
  const t = useT();

  if (!company || !widgetDefinition) return null;

  return (
    <WidgetContainer
      {...props}
      widgetDefinition={{
        ...widgetDefinition,
        title: widgetDefinition.title ?? t("page.alertDetails.googleMaps.title"),
        description: [company.name, company.address].join(", "),
      }}
    >
      {error && <div className="p-4 text-textError">{error}</div>}
      {warning && !error && <div className="p-4 text-textError">{warning}</div>}

      {!error && (company.address || company.name) && (
        <div className="relative w-full h-[500px]">
          <div className="absolute top-4 right-10 z-10">
            <ViewModeSwitchButton viewMode={viewMode} onViewModeChange={setViewMode} />
          </div>

          {viewMode === "street" ? (
            <StreetView center={geocodedLocation ?? placeLocation ?? center} />
          ) : (
            <div className="relative w-full h-full flex flex-col gap-5">
              <MapView
                placeLocation={placeLocation}
                geocodedLocation={geocodedLocation}
                placeDetails={placeDetails}
                geoCoderDetails={geoCoderDetails}
                onMarkerClick={(type: string, data) => {
                  if (type === "place") {
                    setSelectedPlace(data as google.maps.places.PlaceResult);
                    setSelectedGeoCoder(null);
                  } else {
                    setSelectedGeoCoder(data as google.maps.GeocoderResult);
                    setSelectedPlace(null);
                  }
                }}
              />
              {selectedPlace && (
                <MarkerDetails
                  placeDetails={selectedPlace}
                  onClose={() => setSelectedPlace(null)}
                />
              )}
              {selectedGeoCoder && (
                <MarkerDetails
                  companyName={company.name}
                  companyAddress={selectedGeoCoder.formatted_address}
                  onClose={() => setSelectedGeoCoder(null)}
                />
              )}

              <div className="text-sm flex gap-2">
                {placeLocation && (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3" />
                    <img
                      alt={"blue"}
                      src={"http://maps.google.com/mapfiles/ms/icons/blue-dot.png"}
                    />
                    <span>{t("widget.googleMap.legend.blue")}</span>
                  </div>
                )}
                {geocodedLocation && (
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3" />
                    <img alt={"red"} src={"http://maps.google.com/mapfiles/ms/icons/red-dot.png"} />
                    <span>{t("widget.googleMap.legend.red")}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {!error && !company.address && !company.name && (
        <div className="p-7 text-center text-textSecondary">
          {t("page.alertDetails.googleMaps.noAddress")}
        </div>
      )}
    </WidgetContainer>
  );
};

export default GoogleStreetViewWidget;
